import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar as RBar, Cell, PieChart, Pie,
} from "recharts";
import { Activity, AlertTriangle, Package, TrendingUp, Timer, Users } from "lucide-react";
import { api } from "@/lib/api";
import { fmtMoney, fmtNum, fmtDate } from "@/lib/format";
import { Kpi, Panel, Badge2, Bar, PageHead } from "@/components/Bits";

const CHART_AXIS = { fontSize: 11, fontFamily: "IBM Plex Mono" };

export default function Dashboard() {
  const { pid } = useParams();
  const [d, setD] = useState(null);
  const [risk, setRisk] = useState(null);

  useEffect(() => {
    api.get(`/projects/${pid}/dashboard`).then(({ data }) => setD(data));
    api.get(`/projects/${pid}/risk`).then(({ data }) => setRisk(data)).catch(() => setRisk(false));
  }, [pid]);

  if (!d) return <div className="text-sm text-muted-foreground">Computing project intelligence…</div>;
  const { evm, scurve, cpm_summary: cpm, hindrances, store } = d;

  return (
    <div data-testid="dashboard-page">
      <PageHead title="Command Center" subtitle={`${d.project.client} · ${d.project.location} · ${fmtDate(d.project.start_date)} → ${fmtDate(d.project.finish_date)}`}>
        <Badge2 tone={evm.spi >= 0.95 ? "good" : "bad"}>SPI {evm.spi}</Badge2>
        <Badge2 tone={evm.cpi >= 1 ? "good" : "bad"}>CPI {evm.cpi}</Badge2>
        <Link to={`/p/${pid}/reports`} className="text-[13px] text-primary hover:underline" data-testid="dashboard-export-link">Export center →</Link>
      </PageHead>

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        <Kpi label="Physical % complete" value={`${evm.percent_complete}%`} tone="primary" icon={Activity} testId="kpi-percent-complete" sub={`EV ${fmtMoney(evm.ev)} of ${fmtMoney(evm.bac)}`} />
        <Kpi label="Schedule index (SPI)" value={evm.spi} tone={evm.spi >= 0.95 ? "good" : "bad"} icon={Timer} testId="kpi-spi-value" sub={`SV ${fmtMoney(evm.sv)}`} />
        <Kpi label="Cost index (CPI)" value={evm.cpi} tone={evm.cpi >= 1 ? "good" : "bad"} icon={TrendingUp} testId="kpi-cpi-value" sub={`CV ${fmtMoney(evm.cv)}`} />
        <Kpi label="Forecast at completion" value={fmtMoney(evm.eac)} tone={evm.vac >= 0 ? "good" : "bad"} testId="kpi-eac-value" sub={`VAC ${fmtMoney(evm.vac)}`} />
        <Kpi label="Open hindrances" value={hindrances.open} tone={hindrances.open ? "warn" : "good"} icon={AlertTriangle} testId="kpi-hindrances" sub={`${hindrances.impact_days} delay days · ${fmtMoney(hindrances.cost_impact)}`} />
        <Kpi label="Critical stock items" value={store.critical_low} tone={store.critical_low ? "bad" : "good"} icon={Package} testId="kpi-critical-stock" sub={`${store.rfq_pending} RFQ awaiting approval`} />
      </div>

      <div className="grid xl:grid-cols-3 gap-3 mt-3">
        <Panel title="Progress S-Curve — Planned vs Actual vs Forecast" className="xl:col-span-2" testId="scurve-panel">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={scurve} margin={{ top: 6, right: 10, bottom: 0, left: -14 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                <XAxis dataKey="date" tick={CHART_AXIS} tickFormatter={(v) => v.slice(2, 7)} stroke="hsl(var(--muted-foreground))" minTickGap={24} />
                <YAxis tick={CHART_AXIS} stroke="hsl(var(--muted-foreground))" unit="%" domain={[0, 100]} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12, fontFamily: "IBM Plex Mono" }}
                  formatter={(v, n) => [v === null ? "—" : `${v}%`, n]}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="planned_pct" name="Planned" stroke="hsl(var(--muted-foreground))" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="actual_pct" name="Actual" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={false} connectNulls />
                <Line type="monotone" dataKey="forecast_pct" name="Forecast" stroke="#F59E0B" strokeWidth={2} strokeDasharray="5 4" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Earned Value Breakdown" testId="evm-panel">
          <div className="space-y-2">
            {[
              ["Budget at completion (BAC)", evm.bac], ["Planned value (PV)", evm.pv],
              ["Earned value (EV)", evm.ev], ["Actual cost (AC)", evm.ac],
              ["Material / bought-out cost", evm.material_cost], ["Labour cost", evm.labor_cost],
              ["Equipment cost", evm.equipment_cost],
              ["Indirect overheads", evm.indirect_cost], ["Hindrance cost exposure", evm.hindrance_cost],
              ["Estimate to complete (ETC)", evm.etc],
            ].map(([l, v]) => (
              <div key={l} className="flex items-center justify-between gap-3 text-[13px] border-b border-border pb-1.5 last:border-0">
                <span className="text-muted-foreground">{l}</span>
                <span className="num font-medium">{fmtMoney(v)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between text-[13px] pt-1">
              <span className="text-muted-foreground">Manhours consumed</span>
              <span className="num font-medium">{fmtNum(evm.manhours, 0)} hrs</span>
            </div>
          </div>
        </Panel>
      </div>

      <div className="grid xl:grid-cols-3 gap-3 mt-3">
        <Panel
          title="Risk Radar — predictive RAG"
          testId="risk-radar-widget"
          action={<Link to={`/p/${pid}/risk`} className="text-[11px] text-primary hover:underline" data-testid="risk-radar-link">Open radar →</Link>}
        >
          {risk === null ? (
            <p className="text-sm text-muted-foreground">Running predictive model…</p>
          ) : risk === false ? (
            <p className="text-sm text-muted-foreground">Risk model unavailable.</p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <span
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-[13px] font-semibold text-white shrink-0 ${
                    risk.overall_band === "red" ? "bg-red-500" : risk.overall_band === "amber" ? "bg-amber-500" : "bg-emerald-500"
                  }`}
                  data-testid="risk-widget-band"
                >
                  {risk.overall_band.toUpperCase()}
                </span>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Forecasted completion</div>
                  <div className="num text-lg font-semibold leading-tight" data-testid="risk-widget-forecast-date">
                    {risk.schedule.forecast_finish ? fmtDate(risk.schedule.forecast_finish) : "—"}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Contract {fmtDate(risk.schedule.planned_finish)} ·{" "}
                    <span className={(risk.schedule.slip_days ?? 0) > 0 ? "text-red-500" : "text-emerald-500"}>
                      {risk.schedule.slip_days === null ? "—" : `${risk.schedule.slip_days > 0 ? "+" : ""}${risk.schedule.slip_days} d`}
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[["Red", risk.counts.red, "bg-red-500"], ["Amber", risk.counts.amber, "bg-amber-500"], ["Green", risk.counts.green, "bg-emerald-500"]].map(([l, v, c]) => (
                  <div key={l} className="border border-border p-2">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${c}`} />
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</span>
                    </div>
                    <div className="num text-lg font-semibold leading-none mt-1">{v}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-1.5">
                {risk.top_risks.slice(0, 4).map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-[12px]" data-testid={`risk-widget-item-${r.code}`}>
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.risk === "red" ? "bg-red-500" : "bg-amber-500"}`} />
                    <span className="truncate flex-1">{r.name}</span>
                    <span className="num text-muted-foreground shrink-0">{r.days_late === null ? "stalled" : `${r.days_late}d late`}</span>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-muted-foreground border-t border-border pt-2">
                Projected overrun <span className="num text-red-500">{risk.cost.overrun_pct}%</span> · model confidence{" "}
                <span className="num">{risk.confidence_pct}%</span>
              </div>
            </div>
          )}
        </Panel>

        <Panel title="Discipline-wise Progress" testId="discipline-panel">
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.disciplines} layout="vertical" margin={{ left: 4, right: 18 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={CHART_AXIS} unit="%" stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="discipline" tick={{ fontSize: 10 }} width={110} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => `${v}%`} />
                <RBar dataKey="progress_pct" name="Progress" radius={[0, 2, 2, 0]}>
                  {d.disciplines.map((x, i) => (
                    <Cell key={i} fill={x.progress_pct >= 70 ? "#10B981" : x.progress_pct >= 35 ? "hsl(var(--primary))" : "#EF4444"} />
                  ))}
                </RBar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Lagging Quantities (auto-computed balance)" testId="laggards-panel">
          <div className="overflow-x-auto">
            <table className="w-full dense-table">
              <thead><tr><th>Activity</th><th className="text-right">Balance</th><th>Progress</th></tr></thead>
              <tbody>
                {d.laggards.map((a) => (
                  <tr key={a.id}>
                    <td className="max-w-[180px] truncate" title={a.name}>{a.name}</td>
                    <td className="num text-right">{fmtNum(a.balance_qty, 0)} {a.unit}</td>
                    <td><Bar pct={a.progress_pct} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Delay Drivers" testId="delay-drivers-panel">
          {hindrances.by_category.length ? (
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={hindrances.by_category} dataKey="count" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {hindrances.by_category.map((x, i) => (
                      <Cell key={i} fill={["#EF4444", "#F59E0B", "#3B82F6", "#8B5CF6", "#10B981", "#EC4899", "#14B8A6"][i % 7]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No hindrances logged.</p>
          )}
        </Panel>
      </div>

      <div className="grid xl:grid-cols-2 gap-3 mt-3">
        <Panel title={`Critical Path Focus — ${cpm.critical_count} of ${cpm.total_activities} activities (TF = 0)`} testId="critical-panel"
          action={<span className="num text-[11px] text-muted-foreground">{cpm.project_duration} day programme</span>}>
          <div className="overflow-x-auto">
            <table className="w-full dense-table">
              <thead><tr><th>Code</th><th>Activity</th><th className="text-right">Dur</th><th className="text-right">TF</th><th>Progress</th></tr></thead>
              <tbody>
                {d.critical_activities.map((n) => (
                  <tr key={n.id}>
                    <td className="num text-primary">{n.code}</td>
                    <td className="max-w-[220px] truncate" title={n.name}>{n.name}</td>
                    <td className="num text-right">{n.duration}d</td>
                    <td className="num text-right text-red-500">{n.total_float}</td>
                    <td><Bar pct={n.progress_pct} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Store Criticality & Field Log Status" testId="store-status-panel">
          <div className="grid grid-cols-3 gap-2 mb-3">
            <Kpi label="DPRs today" value={d.dpr_today} icon={Users} />
            <Kpi label="DPRs total" value={d.dpr_total} />
            <Kpi label="Stock items" value={store.items} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full dense-table">
              <thead><tr><th>Material</th><th className="text-right">Balance</th><th className="text-right">Min</th><th>Status</th></tr></thead>
              <tbody>
                {store.low_stock_items.map((m) => (
                  <tr key={m.id}>
                    <td className="max-w-[180px] truncate">{m.name}</td>
                    <td className="num text-right">{fmtNum(m.received_qty - m.consumed_qty, 1)} {m.unit}</td>
                    <td className="num text-right">{fmtNum(m.min_stock, 1)}</td>
                    <td><Badge2 tone="bad">Critical low</Badge2></td>
                  </tr>
                ))}
                {store.low_stock_items.length === 0 ? <tr><td colSpan={4} className="text-muted-foreground">All materials above safety stock.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
}
